import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import ReadyToStartProject from "../components/ReadyToStartProject";
import Footer from "../components/Footer";
import AOS from "aos";
import "aos/dist/aos.css"; // Importez les styles de AOS
import { Helmet } from "react-helmet";

const Application = () => {
  const [showMore, setShowMore] = useState({});

  useEffect(() => {
    AOS.init({
      duration: 1000, // Durée de l'animation en ms
      easing: "ease-in-out", // Type d'animation
    });
  }, []);

  const toggleShowMore = (index) => {
    setShowMore((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  };

  const scrollToElement = (text) => {
    document.getElementById(text).scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="bg-gray-900  min-h-screen text-white">
      <Helmet>
        <title>
          Développez des Applications Mobiles Performantes | WebMinds
        </title>
        <meta
          name="description"
          content="Transformez votre idée en une application mobile performante. Nous proposons des services de développement d'applications iOS et Android, design UI/UX, et optimisation des performances."
        />
        <meta
          name="keywords"
          content="applications mobiles, développement mobile, design UI/UX, iOS, Android, services d'application, optimisation mobile"
        />
      </Helmet>

      <Navbar />

      {/* Section d'introduction */}
      <section
        data-aos="fade-in" // Ajoutez l'animation AOS ici
        className="h-screen relative bg-cover bg-center bg-no-repeat"
        style={{ backgroundImage: `url('/images/app.jpg')` }}
      >
        <div className="flex items-center justify-center h-full bg-black bg-opacity-50">
          <div className="text-center text-white max-w-2xl px-6 md:px-12">
            <h1 className="text-5xl font-bold mb-6">
              Réalisez Votre Vision avec une Application Mobile
            </h1>
            <p className="text-2xl mb-6">
              Transformez vos idées en une application mobile performante et
              intuitive.
            </p>
            <p className="text-lg mb-8">
              Notre équipe vous accompagne à chaque étape du développement pour
              créer des applications qui répondent parfaitement à vos besoins.
            </p>
            <a
              href="#services"
              className="mt-4 px-6 py-3 bg-green-500 text-white text-xl font-semibold rounded-md shadow-md hover:bg-green-600 transition duration-300"
              onClick={() => scrollToElement("services")}
            >
              nos services de développement
            </a>
          </div>
        </div>
        <div
          className="absolute bottom-8 left-1/2 transform -translate-x-1/2 text-white cursor-pointer animate-bounce"
          onClick={() => scrollToElement("why-app")}
        >
          <i className="fas fa-chevron-down fa-3x"></i>
        </div>
      </section>

      {/* Section Pourquoi le Développement d'Applications */}
      <section
        data-aos="fade-up"
        id="why-app"
        className="py-16 px-6 md:px-12 lg:px-24 bg-gray-800"
      >
        <h2 className="text-4xl font-bold mb-8 text-center">
          Pourquoi Investir dans une Application Mobile ?
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Augmentez Votre Visibilité
            </h3>
            <p className="text-lg mb-4">
              Une application mobile vous permet d'atteindre un plus grand
              nombre d'utilisateurs et de renforcer la notoriété de votre
              marque.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Améliorez l'Engagement Client
            </h3>
            <p className="text-lg mb-4">
              Offrez une expérience utilisateur améliorée avec des
              fonctionnalités interactives et des notifications push.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">
              Créez des Opportunités de Revenus
            </h3>
            <p className="text-lg mb-4">
              Monétisez votre application à travers des achats intégrés, des
              abonnements ou des publicités.
            </p>
          </div>
          <div>
            <h3 className="text-2xl font-semibold mb-4">Restez Compétitif</h3>
            <p className="text-lg mb-4">
              Avec une application mobile bien développée, vous vous positionnez
              comme un leader dans votre secteur d'activité.
            </p>
          </div>
        </div>
      </section>

      {/* Section des Services de Développement d'Applications */}
      <section
        data-aos="fade-up"
        id="services"
        className="py-16 bg-gray-900 px-6 md:px-12 lg:px-24"
      >
        <h2 className="text-4xl font-bold mb-8 text-center text-yellow-500">
          Nos Services de Développement d'Applications
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Conception d'Applications
            </h3>
            <p className="text-lg mb-4">
              Création d'une interface utilisateur intuitive et attrayante pour
              offrir une expérience optimale.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[0] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous concevons des interfaces qui non seulement sont esthétiques
                mais aussi fonctionnelles, assurant que chaque interaction de
                l'utilisateur est fluide et engageante.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(0)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[0] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Développement Android & iOS
            </h3>
            <p className="text-lg mb-4">
              Développement natif pour Android et iOS pour une performance
              optimale sur chaque plateforme.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[1] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous utilisons les technologies les plus récentes pour
                développer des applications robustes et réactives, adaptées aux
                spécificités de chaque système d'exploitation.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(1)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[1] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Intégration d'APIs
            </h3>
            <p className="text-lg mb-4">
              Intégration avec des services tiers pour enrichir les
              fonctionnalités de votre application.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[2] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous connectons votre application à des APIs externes, que ce
                soit pour des paiements, des cartes géographiques ou d'autres
                services, pour offrir des fonctionnalités enrichies.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(2)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[2] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Tests & Assurance Qualité
            </h3>
            <p className="text-lg mb-4">
              Test approfondi pour assurer la qualité et la performance de votre
              application avant le lancement.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[3] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nos équipes de QA effectuent des tests rigoureux, y compris des
                tests unitaires, d'intégration et de performance, pour garantir
                que votre application est prête à être lancée sans problème.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(3)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[3] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Maintenance & Support
            </h3>
            <p className="text-lg mb-4">
              Support continu et maintenance pour assurer le bon fonctionnement
              de votre application après le lancement.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[4] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Nous proposons des services de maintenance pour garantir que
                votre application reste à jour, sécurisée et performante, même
                après son lancement.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(4)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[4] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>

          <div className="bg-gray-800 rounded-lg shadow-lg p-6 hover:shadow-2xl transition-shadow duration-300">
            <h3 className="text-2xl font-semibold mb-4 text-yellow-300">
              Développement de Fonctionnalités Sur Mesure
            </h3>
            <p className="text-lg mb-4">
              Développement de fonctionnalités spécifiques selon vos besoins
              particuliers.
            </p>
            <div
              className={`transition-all duration-500 ease-in-out overflow-hidden ${
                showMore[5] ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
              }`}
            >
              <p className="text-lg mb-4">
                Que vous ayez besoin d'une fonctionnalité unique ou d'une
                intégration personnalisée, nous développons des solutions sur
                mesure pour répondre exactement à vos besoins.
              </p>
            </div>
            <button
              onClick={() => toggleShowMore(5)}
              className="text-blue-500 hover:text-blue-600 transition duration-300"
            >
              {showMore[5] ? "Lire moins" : "Lire plus"} &rarr;
            </button>
          </div>
        </div>
      </section>
      {/* Section des Tarifs de Développement d'Applications */}
      <section
        data-aos="fade-up"
        id="pricing"
        className="py-20 bg-fixed bg-cover bg-center text-center"
        style={{
          backgroundImage: `url('/images/bg-pricing.jpg')`,
          backgroundAttachment: "fixed",
          backgroundPosition: "center",
        }}
      >
        <div className="bg-gradient-to-r from-gray-800 to-gray-900 py-16">
          <h2 className="text-5xl font-bold mb-12 text-yellow-400">
            Nos Tarifs de Développement
          </h2>
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8 px-4">
            {/* Plan de Base */}
            <div className="bg-gray-800 border-2 border-blue-500 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col relative">
              <div className="absolute top-0 left-0 bg-blue-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Populaire
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait de Base
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">2999€</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Conception d'une application simple
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Développement pour une plateforme
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Déploiement sur le store
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Support de base
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-blue-500 text-white font-semibold rounded-md shadow-lg hover:bg-blue-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
            {/* Plan Avancé */}
            <div className="bg-gray-800 border-2 border-yellow-500 rounded-lg relative shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col">
              <div className="absolute top-0 left-0 bg-yellow-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Le plus Populaire
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait Avancé
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">5999€</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Conception d'application personnalisée
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Développement pour deux plateformes
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Support étendu
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2 mb-5"></i>
                  Intégration d'APIs
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-yellow-500 text-white font-semibold rounded-md shadow-lg hover:bg-yellow-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
            {/* Plan Pro */}
            <div className="bg-gray-800 relative border-2 border-red-500 rounded-lg shadow-lg hover:shadow-2xl transition-shadow duration-300 p-8 flex flex-col">
              <div className="absolute top-0 left-0  bg-red-500 text-white py-2 px-6 text-sm font-bold transform -translate-y-1/2 -translate-x-1/4 rotate-12">
                Exclusif
              </div>
              <h3 className="text-2xl font-semibold mb-6 text-yellow-300">
                Forfait Pro
              </h3>
              <p className="text-5xl font-bold mb-6 text-white">7999€ +</p>
              <ul className="text-left space-y-4">
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Conception complète et avancée
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Développement pour plusieurs plateformes
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2"></i>
                  Support complet et maintenance
                </li>
                <li>
                  <i className="fas fa-check-circle text-green-400 mr-2 mb-5"></i>
                  Analyse et rapports détaillés
                </li>
              </ul>
              <a
                href="/contact"
                className="mt-auto px-8 py-3 bg-red-500 text-white font-semibold rounded-md shadow-lg hover:bg-red-600 transition duration-300"
              >
                Commencer
              </a>
            </div>
          </div>
          <p className="mt-10 text-gray-400 text-center">
            Les tarifs indiqués sont standards et peuvent varier en fonction de
            vos besoins spécifiques. N’hésitez pas à nous contacter pour un
            devis personnalisé.
          </p>
        </div>
      </section>

      <ReadyToStartProject />

      <Footer />
    </div>
  );
};

export default Application;
